/**
 * SuperAdmin Header Component
 * Matches enterprise (admin) header: white bar with title, breadcrumb, and user avatar
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Layout, Avatar, Button, Badge, Empty, List, Popover, Tag, Typography } from 'antd';
import { BellOutlined, MenuOutlined } from '@ant-design/icons';
import superAdminService, { SuperAdminNotification } from '../../services/superAdminService';
import './SuperAdminHeader.css';

const { Header: AntHeader } = Layout;
const { Text } = Typography;

const routeTitles: { [key: string]: string } = {
  '/superadmin': 'Dashboard',
  '/superadmin/companies': 'Company Details',
  '/superadmin/create': 'Add Company',
  '/superadmin/analytics': 'Analytics',
  '/superadmin/settings': 'Settings',
};

interface SuperAdminHeaderProps {
  onMenuClick?: () => void;
  sidebarCollapsed?: boolean;
  isMobile?: boolean;
}

const SuperAdminHeader: React.FC<SuperAdminHeaderProps> = ({
  onMenuClick,
  sidebarCollapsed = false,
  isMobile = false,
}) => {
  const location = useLocation();
  const [notifications, setNotifications] = useState<SuperAdminNotification[]>([]);
  const [notificationCount, setNotificationCount] = useState(0);
  const [notificationsOpen, setNotificationsOpen] = useState(false);

  const getPageTitle = (): string => {
    const path = location.pathname;
    if (routeTitles[path]) return routeTitles[path];
    for (const [route, title] of Object.entries(routeTitles)) {
      if (path.startsWith(route)) return title;
    }
    return 'Dashboard';
  };

  const getBreadcrumb = (): string => {
    return `Home / ${getPageTitle()}`;
  };

  const headerStyle = isMobile
    ? { left: 0, width: '100%' }
    : {
        left: sidebarCollapsed ? 80 : 280,
        width: `calc(100% - ${sidebarCollapsed ? 80 : 280}px)`,
      };

  const loadNotifications = useCallback(async (sendExpiryEmails = false) => {
    try {
      const data = await superAdminService.getSuperAdminNotifications(sendExpiryEmails);
      setNotifications(data.notifications || []);
      setNotificationCount(data.unread_count || 0);
    } catch {
      // Keep the header quiet; auth/network errors are handled globally by apiService.
    }
  }, []);

  useEffect(() => {
    loadNotifications(false);
    const timer = window.setInterval(() => loadNotifications(false), 60000);
    return () => window.clearInterval(timer);
  }, [loadNotifications]);

  const notificationTagColor = (severity: SuperAdminNotification['severity']) => {
    if (severity === 'error') return 'red';
    if (severity === 'warning') return 'gold';
    if (severity === 'success') return 'green';
    return 'blue';
  };

  const notificationContent = useMemo(() => (
    <div className="superadmin-notification-popover">
      <div className="superadmin-notification-header">
        <Text strong>Notifications</Text>
        <Text type="secondary">{notificationCount} active</Text>
      </div>
      {notifications.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No active notifications" />
      ) : (
        <List
          dataSource={notifications}
          renderItem={(item) => (
            <List.Item className="superadmin-notification-item">
              <div>
                <div className="superadmin-notification-title-row">
                  <Text strong>{item.title}</Text>
                  <Tag color={notificationTagColor(item.severity)}>{item.severity.toUpperCase()}</Tag>
                </div>
                <Text type="secondary">{item.message}</Text>
              </div>
            </List.Item>
          )}
        />
      )}
    </div>
  ), [notificationCount, notifications]);

  return (
    <AntHeader className="superadmin-header" style={headerStyle}>
      <div className="superadmin-header-left">
        {isMobile && onMenuClick && (
          <Button
            type="text"
            icon={<MenuOutlined />}
            className="superadmin-header-menu-btn"
            onClick={onMenuClick}
            aria-label="Open menu"
          />
        )}
        <span className="superadmin-header-breadcrumb">{getBreadcrumb()}</span>
      </div>
      <div className="superadmin-header-center">
        <h1 className="superadmin-header-title">{getPageTitle()}</h1>
      </div>
      <div className="superadmin-header-right">
        <Popover
          content={notificationContent}
          trigger="click"
          placement="bottomRight"
          visible={notificationsOpen}
          onVisibleChange={(open) => {
            setNotificationsOpen(open);
            if (open) loadNotifications(true);
          }}
        >
          <Badge count={notificationCount} size="small" overflowCount={99}>
            <Button
              type="text"
              icon={<BellOutlined />}
              className="superadmin-header-icon-btn"
              aria-label="Open notifications"
            />
          </Badge>
        </Popover>
        <div className="superadmin-header-avatar-container">
          <Avatar
            size={40}
            className="superadmin-header-avatar"
            style={{
              background: '#4A90E2',
              color: '#ffffff',
              fontWeight: 600,
              borderRadius: '50%',
            }}
          >
            SA
          </Avatar>
        </div>
      </div>
    </AntHeader>
  );
};

export default SuperAdminHeader;
